/*
 *
 * @providesModule InterstitialModal
 *
 */
import {ISkillzImageProps, Interstitial} from '@skillz/ui';
import AssetHelper from 'AssetHelper';
import {INTERSTITIAL_CLAIMED, INTERSTITIAL_CLOSED, INTERSTITIAL_SHOWN} from 'AWSConstants';
import AWSManager from 'AWSManager';
import CheckoutController from 'CheckoutController';
import DeepLinkUtils from 'DeepLinkUtils';
import I18n, {_m} from 'INTL';
import {debounce, isNumber} from 'lodash';
import ModalNavigationController from 'ModalNavigationController';
import React, {useEffect, useState} from 'react';
import {ImageSourcePropType, Platform, View, NativeModules} from 'react-native';
import SkillzStyle from 'SkillzStyle';
import TrackMetricPerformance from 'TrackMetricPerformance';
import StoreFrontStore from 'StoreFrontStore';
import {OfferInfo} from 'StoreInfo';
import stylesFn from './InterstitialModal.styles';
import Sizes from 'Sizes';
import CurrentUserStore from 'CurrentUserStore';
import {
  InterstitialProps,
  mapAllowedPaymentMethodsToPaymentInfoKind,
  EInterstitialAllowedPaymentMethod,
} from 'InterstitialModalUtils';
// import LiveActivityManager from 'LiveActivityManager';
import {TelemetryEventName} from 'TelemetryConstants';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';

const SkillzModule = NativeModules.SkillzModule;

const HOUSE_AD_IMAGE_PROPS_SMALL_DEVICES_TABLETS: Partial<ISkillzImageProps> = {
  resizeMethod: 'scale',
  resizeMode: 'contain',
};

interface IProps {
  data: InterstitialProps;
  onDismiss?: () => void;
}

const styles = stylesFn();
const InterstitialModal: React.FC<IProps> = ({data, onDismiss}) => {
  const [isClaiming, setIsClaiming] = useState(false);

  useEffect(() => {
    CurrentUserStore.getCurrentUserId().then((id: string) => {
      const awsInterstitialData = JSON.stringify({
        INTERSTITIAL_ID: data.id,
        INTERSTITIAL_TYPE: data.interstitial_type,
        USER_ID: id,
        PAYMENT_TYPE: data.payment_methods?.toString(),
        ACH_ONLY_DEPOSIT:
          data.payment_methods?.length === 1 &&
          data.payment_methods?.includes(EInterstitialAllowedPaymentMethod.BANK_ACCOUNT)
            ? 1
            : 0,
      });
      AWSManager.trackEvent(INTERSTITIAL_SHOWN, {result: awsInterstitialData});
      trackTelemetryEvent({
        event: TelemetryEventName.INTERSTITIAL_SHOWN,
        payload: {
          interstitial_id: data.id,
          interstitial_type: data.interstitial_type,
        },
      });
    });
  }, []);

  const executeDeepLinks = (shouldExecute: boolean) => {
    if (!shouldExecute) {
      return;
    }
    /**
     * Android does not enable the deep link system until the welcome back flow is completed.
     *
     * If multiple interstitials are triggered on WelcomeBack flow and the first one triggers a deep link,
     * it prevents the next WB steps to be shown (as we likely don't want to see rating prompt on store page).
     * Therefore the last WB step to mark welcome back as completed is not called.
     */
    if (Platform.OS === 'android') {
      const methodExists = typeof SkillzModule.setWelcomeBackWasCompleted === 'function';
      if (methodExists) {
        SkillzModule.setWelcomeBackWasCompleted(true);
      }
    }

    /**
     * DeepLinkUtils.handleDeeplink is not enough to handle the deep link on iOS.
     * We need to execute the pending URL linking calling a native method `SkillzModule.openPendingLink`
     * to handle the deep link properly.
     */
    if (Platform.OS === 'ios') {
      DeepLinkUtils.executePendingURLLinking();
    }
  };

  const handleClose = (onError?: boolean, hasTriggeredDeepLink = false) => {
    if (!onError) {
      AWSManager.trackEvent(INTERSTITIAL_CLOSED, {
        category: 'LTO',
        id: data?.id,
      });
      // We are tracking the close event from the close button, not the deep link(claim handler).
      if(!hasTriggeredDeepLink) {
      trackTelemetryEvent({
          event: TelemetryEventName.INTERSTITIAL_CTA_CLICK,
          payload: {
            interstitial_id: data?.id,
            interstitial_type: data?.interstitial_type,
            cta: 'close',
          },
        });
      }
    }
    // If a deep link is triggered, we want to clean the modal stack, suppressing the LTO from being shown.
    const cleanModalStack = hasTriggeredDeepLink;
    ModalNavigationController.hideModal(
      () => {
        executeDeepLinks(hasTriggeredDeepLink);
        onDismiss?.();
      },
      undefined,
      cleanModalStack
    );
  };

  // debounce is used to prevent handleClaim from running
  // if the user taps the claim button multiple times
  const handleClaim = debounce(
    () => {
      if (data.interstitial_type === 'HOUSE_AD') {
        AWSManager.trackEvent(INTERSTITIAL_CLAIMED, {
          category: 'HOUSE_AD',
          id: data?.id,
        });
        trackTelemetryEvent({
          event: TelemetryEventName.INTERSTITIAL_CTA_CLICK,
          payload: {
            interstitial_id: data?.id,
            interstitial_type: data?.interstitial_type,
            cta: 'claim',
          },
        });
      }

      if (data.deeplink_android && Platform.OS === 'android') {
        // Check for and handle OTA deep links with android
        if (DeepLinkUtils._shouldHandleDeeplinkOTA(data.deeplink_android)) {
          DeepLinkUtils._handleOTADeeplink(data.deeplink_android);
          handleClose(undefined, true);
        } else {
          DeepLinkUtils.handleDeeplink(data.deeplink_android, () => {
            handleClose(undefined, true);
          });
        }
        return;
      }

      if (data.deeplink_ios && Platform.OS === 'ios') {
        DeepLinkUtils.handleDeeplink(data.deeplink_ios, () => {
          handleClose(undefined, true);
        });
        return;
      }

      if (Platform.OS === 'web' && (data.deeplink_ios || data.deeplink_android)) {
        DeepLinkUtils.handleDeeplink(data.deeplink_ios || data.deeplink_android || '', () => {
          handleClose(undefined, true);
        });
        return;
      }

      if (CheckoutController.isLaunchingCheckout()) {
        return;
      }

      setIsClaiming(true);
      TrackMetricPerformance.trackMetric({
        name: 'CLAIM_INTERSTITIAL_PROCESS',
        category: 'LTO',
        timeOut: 10000,
      });
      const offer: OfferInfo = {
        id: String(data.id),
        amount: data.deposit_amount || 0,
        bonus: data.bonus_cash || 0,
        best_value: false,
        is_interstitial_purchase: true,
        ticketz: data.ticketz || 0,
        points: data.points || 0,
        interstitial_type: data.interstitial_type,
      };
      StoreFrontStore.fetchDepositOffersWithCompletion(true, (storeInfo: any) => {
        TrackMetricPerformance.addFinishedStep({
          name: 'CLAIM_INTERSTITIAL_PROCESS',
          step: 'fetchDepositOffersWithCompletion',
        });
        const isAchOnlyDeposit =
          data.payment_methods?.length === 1 &&
          data.payment_methods?.includes(EInterstitialAllowedPaymentMethod.BANK_ACCOUNT);
        storeInfo.isACHOnlyDeposit = isAchOnlyDeposit;
        AWSManager.trackEvent(INTERSTITIAL_CLAIMED, {
          category: 'LTO',
          id: data?.id,
        });
        trackTelemetryEvent({
          event: TelemetryEventName.INTERSTITIAL_CTA_CLICK,
          payload: {
            interstitial_id: data?.id,
            interstitial_type: data?.interstitial_type,
            cta: 'claim',
          },
        });
        CheckoutController.launchCheckoutFlow({
          offer,
          storeInfo,
          verifyEligibilityDuringCheckout: true,
          onCheckoutDisplayed: handleClose,
          allowedPaymentInfoKinds: mapAllowedPaymentMethodsToPaymentInfoKind(
            data.payment_methods ?? []
          ),
          isACHOnlyDeposit: isAchOnlyDeposit,
          deposit_banner_img_url: data?.deposit_banner_img_url,
        }).finally(() => {
          TrackMetricPerformance.completeMetric('CLAIM_INTERSTITIAL_PROCESS');
        });
      });
    },
    20000,
    {leading: true, trailing: false}
  );

  const isDailyBonusLTO = data.interstitial_type === 'DAILY_BONUS';
  const dailyBonusLTOStatus = data.user_interstitial_state;
  const isDailyBonusOngoing = isDailyBonusLTO && dailyBonusLTOStatus === 'ONGOING';
  const isDailyBonusAvailable = isDailyBonusLTO && (dailyBonusLTOStatus === 'AVAILABLE' || dailyBonusLTOStatus === 'ACTIVE');

  const shouldUseNewCashImage =
    isNumber(data.deposit_amount) &&
    isNumber(data.bonus_cash) &&
    data.bonus_cash > 0 &&
    ((isNumber(data.points) && data.points > 0 && (!data.ticketz || data.ticketz === 0)) ||
      (isNumber(data.ticketz) && data.ticketz > 0 && (!data.points || data.points === 0)));

  // Use lto_cash.png for deferred bonus cases (Daily Bonus LTO with deferredBonus)
  const shouldUseLtoCashForDeferredBonus =
    isDailyBonusLTO &&
    data.deferredBonus !== undefined &&
    data.deferredBonus !== null &&
    data.deferredBonus > 0;

  const shouldUseNewTicketzImage =
    isNumber(data.deposit_amount) &&
    isNumber(data.bonus_cash) &&
    data.bonus_cash > 0 &&
    isNumber(data.ticketz) &&
    data.ticketz > 0 &&
    (!data.points || data.points === 0);

  const cash_image: ImageSourcePropType = {
    uri: AssetHelper.pathForAssetNamed(
      shouldUseNewCashImage || shouldUseLtoCashForDeferredBonus ? 'lto_cash.png' : 'cash.png',
      false
    ),
  };

  const ticketz_image: ImageSourcePropType = {
    uri: AssetHelper.pathForAssetNamed(
      shouldUseNewTicketzImage ? 'lto_ticketz.png' : 'ic_ticketz_background.png',
      false
    ),
  };

  const z_image: ImageSourcePropType = {
    uri: AssetHelper.pathForAssetNamed('z_stretched.png', false),
  };

  const heroImage: ImageSourcePropType = {
    uri: AssetHelper.pathForAssetNamed(data.hero_img_url, false),
  };

  const lto_plus_image: ImageSourcePropType = {
    uri: AssetHelper.pathForAssetNamed('lto_plus.png', false),
  };

  const isLTO = isNumber(data.deposit_amount);

  const isHouseAd = data.interstitial_type === 'HOUSE_AD';
  
  const modalOverlayStyleOverride = undefined;

  const heroBgStyleOverride = isHouseAd
    ? Sizes.dynamic({
        sm: styles.houseAdHeroBgSmallDevicesTablets,
        md: undefined,
        tablet: styles.houseAdHeroBgSmallDevicesTablets,
      })
    : undefined;

  const heroImagePropsOverride = isHouseAd
    ? Sizes.dynamic({
        sm: HOUSE_AD_IMAGE_PROPS_SMALL_DEVICES_TABLETS,
        md: undefined,
        tablet: HOUSE_AD_IMAGE_PROPS_SMALL_DEVICES_TABLETS,
      })
    : undefined;

  const shouldHideSalesSection = data.interstitial_type === 'NO_OFFER_TEXT_LTO';
  
  // For Daily Bonus LTO ONGOING state, use daily_reward_hero_url if available
  const dailyBonusHeroImage: ImageSourcePropType | undefined = isDailyBonusOngoing && data.daily_reward_hero_url
    ? {
        uri: AssetHelper.pathForAssetNamed(data.daily_reward_hero_url, false),
      }
    : undefined;
  
  const finalHeroImage = dailyBonusHeroImage || heroImage;
  
  // useEffect(() => {
  //   // LiveActivityManager.startLTOLiveActivity(data);
  // }, [data]);

  return (
    <View style={styles.backdrop}>
      <View style={styles.container}>
        <Interstitial
          {...data}
          isClaiming={isClaiming}
          confetti={isLTO}
          button_copy_text={isClaiming ? _m('Claiming...') : data.button_copy_text}
          closeDisabled={isClaiming}
          onClose={handleClose}
          onClaim={handleClaim}
          cash_bonus_asset_image={cash_image}
          deposit_asset_image={cash_image}
          interstitial_asset_image={finalHeroImage}
          ticketz_asset_image={ticketz_image}
          z_asset_image={z_image}
          fontFamilyOverride={SkillzStyle.fullStyles.fontExtraBold?.fontFamily}
          animatedButton={isLTO}
          shouldTriggerCTAOnContentPress={isHouseAd}
          shouldHideSalesSection={shouldHideSalesSection}
          modalOverlayStyleOverride={modalOverlayStyleOverride}
          heroBgStyleOverride={heroBgStyleOverride}
          heroImagePropsOverride={heroImagePropsOverride}
          lto_plus_image={lto_plus_image}
          // Daily Bonus LTO props
          isDailyBonusLTO={isDailyBonusLTO}
          // Pass deferredBonus whenever it exists (for new UI layout)
          deferredBonus={data.deferredBonus !== undefined && data.deferredBonus !== null && data.deferredBonus > 0 ? data.deferredBonus : undefined}
          // Pass dailyRewardDurationDays whenever it exists
          dailyRewardDurationDays={data.dailyRewardDurationDays !== undefined && data.dailyRewardDurationDays !== null ? data.dailyRewardDurationDays : undefined}
        />
      </View>
    </View>
  );
};
export default InterstitialModal;
